import { useCallback, useEffect, useRef, useState } from "react";
import { useFeatureAccess } from "./useFeatureAccess";
import { useFeatureFlag } from "../services/featureFlags/FeatureFlagProvider";

// @ts-ignore - Worker import
import CountdownWorker from "../worker/countdown.worker";

interface RecordingLimitState {
  isLimited: boolean;
  durationSeconds: number | null;
  remainingSeconds: number | null;
  isCountdownActive: boolean;
  sessionId: string | null;
}

export interface UseRecordingLimitResult extends RecordingLimitState {
  startCountdown: () => void;
  stopCountdown: () => void;
  pauseCountdown: () => void;
  resumeCountdown: () => void;
  resetCountdown: () => void;
  resetCountdownForNewRecording: () => void;
  formatTime: (seconds: number) => string;
  /** Total user limit in seconds (before subtracting used duration) */
  totalLimitSeconds: number | null;
  /** Already used duration in seconds (from other scenes) */
  usedDurationSeconds: number;
  /** Whether limit is exhausted (no remaining time) */
  isLimitExhausted: boolean;
  /** Update already used duration from project scenes */
  setUsedDuration: (usedSeconds: number) => void;
}

export interface UseRecordingLimitOptions {
  /** Duration already used by other recordings (in seconds) - for cumulative project limits */
  usedDurationSeconds?: number;
}

const UNLIMITED_DURATION = 999999999;
const DEFAULT_FREE_LIMIT_MINUTES = 5;

/**
 * Hook to manage recording duration limits with countdown timer
 * 
 * This hook enforces recording time limits based on user tier. It uses a Web Worker
 * for accurate countdown timing that continues even when the main thread is throttled.
 * 
 * Features:
 * - Countdown timer with pause/resume support
 * - Auto-stops recording when limit reached
 * - Unlimited users bypass countdown entirely
 * - Converts minute-based limits to seconds
 * 
 * @example
 * ```tsx
 * const { isLimited, remainingSeconds, startCountdown, formatTime } = useRecordingLimit();
 * 
 * // Start recording
 * if (isLimited) {
 *   startCountdown(); // Starts countdown from user's limit
 * }
 * 
 * // Display countdown
 * {isLimited && remainingSeconds !== null && (
 *   <div>{formatTime(remainingSeconds)}</div>
 * )}
 * ```
 */
export const useRecordingLimit = (): UseRecordingLimitResult => {
  const limitEnabled = useFeatureFlag('recording-duration-limits');
  const { getLimit, isPaid } = useFeatureAccess();
  
  const [state, setState] = useState<RecordingLimitState>({
    isLimited: false,
    durationSeconds: null,
    remainingSeconds: null,
    isCountdownActive: false,
    sessionId: null,
  });
  
  // Track total limit (before subtracting used) and used duration separately
  const [totalLimitSeconds, setTotalLimitSeconds] = useState<number | null>(null);
  const [usedDurationSeconds, setUsedDurationSeconds] = useState<number>(0);

  const workerRef = useRef<Worker | null>(null);
  const isWorkerInitializedRef = useRef(false);

  // Initialize worker
  useEffect(() => {
    if (!isWorkerInitializedRef.current) {
      try {
        workerRef.current = new CountdownWorker();
        isWorkerInitializedRef.current = true;

        if (workerRef.current) {
          workerRef.current.onmessage = (event: MessageEvent) => {
            const { type, remainingSeconds, sessionId } = event.data;

            switch (type) {
              case 'tick':
                setState(prev => ({
                  ...prev,
                  remainingSeconds,
                }));
                break;

              case 'complete':
                setState(prev => ({
                  ...prev,
                  remainingSeconds: 0,
                  isCountdownActive: false,
                }));
                console.info(`Recording limit reached for session: ${sessionId}`);
                break;

              case 'error':
                console.error('Countdown worker error:', event.data);
                setState(prev => ({
                  ...prev,
                  isCountdownActive: false,
                }));
                break;
            }
          };

          workerRef.current.onerror = (error) => {
            console.error('Countdown worker error:', error);
            setState(prev => ({
              ...prev,
              isCountdownActive: false,
            }));
          };
        }
      } catch (error) {
        console.error('Failed to initialize countdown worker:', error);
      }
    }

    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
        isWorkerInitializedRef.current = false;
      }
    };
  }, []);

  // Check user's recording limit
  const checkRecordingLimit = useCallback(() => {
    if (!limitEnabled) {
      // Feature flag disabled
      setTotalLimitSeconds(null);
      setState(prev => ({
        ...prev,
        isLimited: false,
        durationSeconds: null,
        remainingSeconds: null,
      }));
      return;
    }

    const durationLimit = getLimit("DURATION_PER_VIDEO");
    
    // For non-paid users, use default limit if no specific limit is defined
    if (durationLimit === null) {
      if (!isPaid) {
        // Free user without specific limit - use default 5 minutes
        const totalSeconds = DEFAULT_FREE_LIMIT_MINUTES * 60;
        const availableSeconds = Math.max(0, totalSeconds - usedDurationSeconds);
        setTotalLimitSeconds(totalSeconds);
        setState(prev => ({
          ...prev,
          isLimited: true,
          durationSeconds: availableSeconds,
          remainingSeconds: availableSeconds,
        }));
      } else {
        // Paid user without limit defined - treat as unlimited
        setTotalLimitSeconds(null);
        setState(prev => ({
          ...prev,
          isLimited: false,
          durationSeconds: null,
          remainingSeconds: null,
        }));
      }
      return;
    }

    if (durationLimit >= UNLIMITED_DURATION) {
      // Unlimited recording
      setTotalLimitSeconds(null);
      setState(prev => ({
        ...prev,
        isLimited: false,
        durationSeconds: null,
        remainingSeconds: null,
      }));
    } else {
      // Finite limit - convert minutes to seconds and subtract used duration
      const totalSeconds = durationLimit * 60;
      const availableSeconds = Math.max(0, totalSeconds - usedDurationSeconds);
      setTotalLimitSeconds(totalSeconds);
      setState(prev => ({
        ...prev,
        isLimited: true,
        durationSeconds: availableSeconds,
        remainingSeconds: availableSeconds,
      }));
    }
  }, [limitEnabled, getLimit, isPaid, usedDurationSeconds]);

  // Reset countdown when starting a new recording
  const resetCountdownForNewRecording = useCallback(() => {
    if (state.isLimited && state.durationSeconds) {
      setState(prev => ({
        ...prev,
        remainingSeconds: state.durationSeconds,
      }));
    }
  }, [state.isLimited, state.durationSeconds]);

  // Check limit when feature check becomes available - only run once
  useEffect(() => {
    checkRecordingLimit();
  }, [checkRecordingLimit]);

  const startCountdown = useCallback(() => {
    if (!state.isLimited || !state.durationSeconds) {
      return;
    }

    const sessionId = crypto.randomUUID();
    
    setState(prev => ({
      ...prev,
      isCountdownActive: true,
      sessionId,
    }));

    if (workerRef.current && state.durationSeconds) {
      workerRef.current.postMessage({
        type: 'start',
        duration: state.durationSeconds,
        sessionId,
      });
    }

    console.info(`Started recording countdown: ${state.durationSeconds}s for session: ${sessionId}`);
  }, [state.isLimited, state.durationSeconds]);

  const stopCountdown = useCallback(() => {
    setState(prev => ({
      ...prev,
      isCountdownActive: false,
      // Don't reset remainingSeconds - keep the current value (0 when countdown completes)
      // This prevents the timer from jumping back to the original duration
      sessionId: null,
    }));

    if (workerRef.current) {
      workerRef.current.postMessage({
        type: 'stop',
      });
    }

    console.info('Stopped recording countdown');
  }, []);

  const resetCountdown = useCallback(() => {
    setState(prev => ({
      ...prev,
      isCountdownActive: false,
      remainingSeconds: state.durationSeconds,
      sessionId: null,
    }));

    if (workerRef.current) {
      workerRef.current.postMessage({
        type: 'stop',
      });
    }

    console.info('Reset recording countdown');
  }, [state.durationSeconds]);

  const pauseCountdown = useCallback(() => {
    if (!state.isCountdownActive) return;

    setState(prev => ({
      ...prev,
      isCountdownActive: false,
    }));

    if (workerRef.current) {
      workerRef.current.postMessage({
        type: 'pause',
      });
    }

    console.info('Paused recording countdown');
  }, [state.isCountdownActive]);

  const resumeCountdown = useCallback(() => {
    if (state.isCountdownActive || !state.durationSeconds) return;

    setState(prev => ({
      ...prev,
      isCountdownActive: true,
    }));

    if (workerRef.current) {
      workerRef.current.postMessage({
        type: 'resume',
      });
    }

    console.info('Resumed recording countdown');
  }, [state.isCountdownActive, state.durationSeconds]);

  const formatTime = useCallback((seconds: number): string => {
    if (seconds < 0) return "00:00";
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);
  
  // Function to update used duration (called when project scenes change)
  const setUsedDuration = useCallback((usedSeconds: number) => {
    setUsedDurationSeconds(usedSeconds);
  }, []);
  
  // Check if limit is exhausted (no recording time left)
  const isLimitExhausted = state.isLimited && state.durationSeconds !== null && state.durationSeconds <= 0;

  return {
    ...state,
    startCountdown,
    stopCountdown,
    resetCountdown,
    resetCountdownForNewRecording,
    pauseCountdown,
    resumeCountdown,
    formatTime,
    totalLimitSeconds,
    usedDurationSeconds,
    isLimitExhausted,
    setUsedDuration,
  };
};
